import React, { useCallback, useEffect, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { useHistory } from "react-router-dom";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorValidationMessage } from './ErrorValidationMessage';

import api from '../api/api';

import '../layout/sass/_form.scss';
import { FormPessoaMudas } from './FormPessoaMudas';
import { TabelaPessoaMudas } from './TabelaPessoaMudas';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import LogoAgricultura from '../assets/Logo_Agricultura.png';
import LogoIEF from '../assets/Logo_IEF.png';
import { FileUpload } from 'primereact/fileupload';

// Helper para converter imagem em DataURL seguro (PNG) para o pdfmake
const imageToDataURL = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = (err) => reject(err);
        img.src = url;
    });
};

export const FormPessoaPomarDomestico = ({ setSolicitarMudas, cpf, somenteComprovante }) => {
    const history = useHistory();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [mudas, setMudas] = useState([]);
    const [pessoaMudas, setPessoaMudas] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSemDocs, setShowSemDocs] = useState(false);
    const [showDocs, setShowDocs] = useState(false);
    const [docsAnexo, setDocsAnexo] = useState([]);
    const [listMudas, setListMudas] = useState([]);
    const [quantidadeTotal, setQuantidadeTotal] = useState(0);
    const [campanhaNome, setCampanhaNome] = useState("");
    const [validarEnderecoPessoa, setValidarEnderecoPessoa] = useState(false);
    const [campanhaDataEntrega, setCampanhaDataEntrega] = useState("");
    const [campanhaQuantidadeRural, setCampanhaQuantidadeRural] = useState(null);
    const [campanhaQuantidadeUrbano, setCampanhaQuantidadeUrbano] = useState(null);
    const [campanhaEndereco, setCampanhaEndereco] = useState(null);
    const [campanhaMudas, setCampanhaMudas] = useState([]);
    const [disabled, setDisabled] = useState(false);
    const [selecionar, setSelecionar] = useState({ label: 'Selecionar', icon: 'pi pi-fw pi-plus' });
    const [enviar, setEnviar] = useState({ label: 'Enviar', icon: 'pi pi-upload' });
    const cancelar = { label: 'Cancelar', icon: 'pi pi-times' };
    const fileUploadRef = useRef(null);

    useEffect(() => {
        const total = pessoaMudas.reduce((acc, item) => acc + Number(item.quantidade || 0), 0);
        setQuantidadeTotal(total);
    }, [pessoaMudas]);

    const schema = Yup.object().shape({
        cpf: Yup.string()
            .required("O CPF é obrigatório"),
        nome: Yup.string()
            .required("O nome é obrigatório")
            .max(200, "O nome deve ter no máximo 200 caracteres"),
        email: Yup.string()
            .email("Informe um e-mail válido")
            .required("O e-mail é obrigatório"),
        rural: Yup.boolean()
            .required("A seleção da Zona (Urbana ou Rural) é obrigatória"),
        endereco_ou_fazenda: Yup.string()
            .when('rural', {
                is: true,
                then: Yup.string().required("O nome da Fazenda é obrigatório"),
                otherwise: Yup.string().when('rural', {
                    is: false,
                    then: Yup.string().required("O Endereço é obrigatório"),
                    otherwise: Yup.string()
                })
            }),
        bairro_ou_distrito: Yup.string()
            .when('rural', {
                is: true,
                then: Yup.string().required("O Distrito é obrigatório"),
                otherwise: Yup.string().when('rural', {
                    is: false,
                    then: Yup.string().required("O Bairro é obrigatório"),
                    otherwise: Yup.string()
                })
            }),
        numero_instalacao: Yup.string()
            .when('validarEnderecoPessoa', {
                is: true,
                then: Yup.string().required("O Nº da instalação é obrigatório"),
                otherwise: Yup.string()
            }),
        arquivo: Yup.mixed()
            .when('validarEnderecoPessoa', {
                is: true,
                then: Yup.mixed().required("É obrigatório anexar o comprovante de endereço"),
                otherwise: Yup.mixed()
            })
    });

    const formik = useFormik({
        initialValues: {
            'nome': '',
            'cpf': '',
            'email': '',
            'numero_instalacao': '',
            'arquivo': '',
            'campanhaMudas': '',
            'rural': '',
            'endereco_ou_fazenda': '',
            'bairro_ou_distrito': '',
            'validarEnderecoPessoa': false
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (data) => {
            if (validarEnderecoPessoa) {
                if (!data.numero_instalacao || !data.arquivo) {
                    toast.current.show({ severity: 'error', summary: 'Campo obrigatório', detail: 'Número da instalação e Comprovante de Endereço são obrigatórios' });
                    return;
                }
            }

            setIsSubmiting(true);
            data.campanhaMudas = pessoaMudas;

            try {
                const limiteZona = data.rural ? campanhaQuantidadeRural : campanhaQuantidadeUrbano;
                const nomeZona = data.rural ? 'zona rural' : 'zona urbana';

                if (data.campanhaMudas.length === 0) {
                    toast.current.show({ life: 2000, severity: 'error', summary: 'Nenhuma muda selecionada!', detail: `Por favor selecione as mudas para continuar.` });
                    setIsSubmiting(false);
                    return;
                }

                if (quantidadeTotal > limiteZona) {
                    toast.current.show({ life: 4000, severity: 'error', summary: `São permitidas somente ${limiteZona} mudas para a ${nomeZona}! Foram selecionadas ${quantidadeTotal} mudas.` });
                    setIsSubmiting(false);
                    return;
                }

                if (!isEditing) {
                    await api.post("pessoa", data);
                } else {
                    let campanhaMudasInicial = campanhaMudas;
                    let campanhaMudasFinal = data.campanhaMudas;

                    const tempCampanhaMudas = campanhaMudasInicial.map((obj) => {
                        const mesmaMuda = campanhaMudasFinal.find((item) => item.campanhaMuda.id === obj.campanhaMudaId);
                        if (mesmaMuda) {
                            return { ...obj, campanhaMuda: { muda: mesmaMuda.campanhaMuda.muda } };
                        }
                        return null;
                    }).filter(Boolean);

                    const campanhaMudasFinalFiltrada = campanhaMudasFinal.filter((item) =>
                        !campanhaMudasInicial.some((obj) => obj.campanhaMudaId === item.campanhaMuda.id)
                    );

                    const campanhaMudasAtualizado = [...tempCampanhaMudas, ...campanhaMudasFinalFiltrada.map((item) => ({
                        quantidade: parseInt(item.quantidade),
                        campanhaId: item.campanhaMuda.campanhaId,
                        campanhaMudaId: item.campanhaMuda.id,
                        campanhaMuda: { muda: item.campanhaMuda.muda },
                    }))];

                    data.campanhaMudas = campanhaMudasAtualizado;
                    await api.put("pessoa", data);
                }

                formik.resetForm();
                toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro concluído!', detail: `Solicitação realizada com sucesso!` });
                toast.current.show({ life: 5000, severity: 'success', summary: 'Comprovante salvo com sucesso!', detail: `Você será redirecionado para a página principal` });
                
                const win = window.open('', '_blank');
                gerarComprovanteEmJanela(data, win);
                
                setTimeout(() => {
                    setSolicitarMudas(false);
                }, 5000);

            } catch (error) {
                try {
                    const response = await api.post("pessoa/mudaIndisponivelMensagem", data);
                    const mensagemTexto = typeof response.data === 'string' ? response.data : (response.data?.detail || '');

                    const match = mensagemTexto.match(/A muda (.*?) não está mais disponível/i);
                    const nomeMudaIndisponivel = match ? match[1].trim() : null;

                    if (nomeMudaIndisponivel) {
                        setPessoaMudas(prevMudas => {
                            const listaFiltrada = prevMudas.filter(item => {
                                const nomeItem = item.campanhaMuda?.muda?.nome || item.muda?.nome || item.nome;
                                return nomeItem?.toUpperCase() !== nomeMudaIndisponivel.toUpperCase();
                            });

                            const novoTotal = listaFiltrada.reduce((acc, curr) => acc + (parseInt(curr.quantidade, 10) || 0), 0);
                            if (typeof setQuantidadeTotal === 'function') {
                                setQuantidadeTotal(novoTotal);
                            }

                            return listaFiltrada;
                        });
                    }

                    toast.current.show({
                        life: 5000,
                        severity: 'error',
                        summary: isEditing ? 'Erro ao editar pessoa!' : 'Erro ao cadastrar pessoa!',
                        detail: `${mensagemTexto} A muda foi removida automaticamente da sua lista.`
                    });

                } catch (err) {
                    console.error("Erro no tratamento da muda indisponível:", err);
                    toast.current.show({
                        life: 4000,
                        severity: 'error',
                        summary: 'Erro ao processar solicitação',
                        detail: 'Ocorreu um erro ao salvar as mudas.'
                    });
                }
            } finally {
                setIsSubmiting(false);
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    async function handleCpfComplete(cpfParam) {
        let win = null;
        if (somenteComprovante) {
            win = window.open('', '_blank');
        }

        setIsLoading(true);
        try {
            const perfilExiste = await api.get(`pessoa/perfilExiste/${cpfParam}`);

            if (perfilExiste.data === true) {
                const response = await api.get(`pessoa/cpf/${cpfParam}`);
                const pessoaData = response.data;

                formik.setValues({ ...pessoaData, validarEnderecoPessoa });
                setCampanhaMudas(pessoaData.campanhaMudas || []);
                const campanhaMudasResp = pessoaData.campanhaMudas || [];

                let dataEntregaTemp = "";
                let enderecoTemp = "";
                let nomeCampanhaTemp = "";

                const mudasCarregadas = await Promise.all(
                    campanhaMudasResp.map(async (campanhaMuda) => {
                        const resp = await api.get(`campanhaMudas/${campanhaMuda.campanhaMudaId}`);
                        
                        if (resp.data?.campanha) {
                            if (resp.data.campanha.dataEntrega) dataEntregaTemp = resp.data.campanha.dataEntrega;
                            if (resp.data.campanha.endereco) enderecoTemp = resp.data.campanha.endereco;
                            if (resp.data.campanha.nome) nomeCampanhaTemp = resp.data.campanha.nome;
                        }

                        return {
                            campanhaMuda: resp.data,
                            quantidade: campanhaMuda.quantidade
                        };
                    })
                );

                if (dataEntregaTemp) setCampanhaDataEntrega(dataEntregaTemp);
                if (enderecoTemp) setCampanhaEndereco(enderecoTemp);
                if (nomeCampanhaTemp) setCampanhaNome(nomeCampanhaTemp);

                setPessoaMudas(mudasCarregadas);
                setIsEditing(true);

                toast.current.show({
                    life: 3000,
                    severity: 'success',
                    summary: 'Cadastro encontrado!',
                    detail: somenteComprovante ? 'Abrindo comprovante PDF...' : 'Dados preenchidos automaticamente'
                });

                if (somenteComprovante) {
                    const dadosCompletos = {
                        ...pessoaData,
                        campanhaMudas: mudasCarregadas,
                        dataEntrega: dataEntregaTemp,
                        enderecoEntrega: enderecoTemp,
                        nomeCampanha: nomeCampanhaTemp
                    };
                    gerarComprovanteEmJanela(dadosCompletos, win);
                }

            } else {
                if (win) win.close();
                formik.setFieldValue("cpf", cpfParam);
                if (somenteComprovante) {
                    toast.current.show({
                        life: 4000,
                        severity: 'warn',
                        summary: 'Solicitação não encontrada',
                        detail: 'Não existem registros para o CPF informado.'
                    });
                }
            }
        } catch (error) {
            if (win) win.close();
            console.error("Erro ao buscar perfil por CPF:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleGetMudas = useCallback(async () => {
        if (somenteComprovante) return;

        const campanhaAtiva = await api.get(`campanha/ativoCampanha`);
        const response = await api.get(`campanhaMudas/saldo/${campanhaAtiva.data.id}`);
        let tempMudas = [];
        response.data.forEach(muda => {
            if (muda.saldo > 0) {
                tempMudas.push(muda.campanhaMudas.muda);
            }
        });
        setListMudas(tempMudas);

        if (cpf !== "") {
            const perfilExiste = await api.get(`pessoa/perfilExiste/${cpf}`);
            if (perfilExiste.data === true) {
                const perfil = await api.get(`pessoa/cpf/${cpf}`);
                const campanhaMudasResp = perfil.data.campanhaMudas || [];
                const mudasCarregadas = await Promise.all(
                    campanhaMudasResp.map(async (campanhaMuda) => {
                        const resp = await api.get(`campanhaMudas/${campanhaMuda.campanhaMudaId}`);
                        return {
                            campanhaMuda: resp.data,
                            quantidade: campanhaMuda.quantidade
                        };
                    })
                );
                const IdsMudasSolicitadas = mudasCarregadas.map(item => item.campanhaMuda.muda.id);
                const listaAtualizada = tempMudas.filter(item => !IdsMudasSolicitadas.includes(item.id));
                setListMudas(listaAtualizada);
            }
        }
    }, [cpf, somenteComprovante]);

    async function getCampanhaNome() {
        try {
            const response = await api.get(`campanha/ativoCampanha`);
            setCampanhaNome(response.data.nome);
            setCampanhaDataEntrega(response.data.dataEntrega);
            setCampanhaEndereco(response.data.endereco);
            setValidarEnderecoPessoa(response.data.validarEnderecoPessoa);
            formik.setFieldValue("validarEnderecoPessoa", response.data.validarEnderecoPessoa);
        } catch (error) {
            console.error("Erro ao carregar dados da campanha ativa:", error);
        }
    }

    async function getCampanhaQuantidades() {
        try {
            const response = await api.get(`campanha/ativoCampanha`);
            setCampanhaQuantidadeRural(response.data.qtde_mudas_rural);
            setCampanhaQuantidadeUrbano(response.data.qtde_mudas_urbana);
        } catch (error) {
            console.error("Erro ao carregar quantidades da campanha:", error);
        }
    }

    useEffect(() => {
        const carregarDados = async () => {
            try {
                await handleGetMudas();
                await getCampanhaNome();
                await getCampanhaQuantidades();

                if (cpf && cpf.length === 11) {
                    await handleCpfComplete(cpf);
                }
            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
                toast.current?.show({
                    severity: "error",
                    summary: "Erro ao carregar dados",
                    detail: error.toString(),
                    life: 4000
                });
            }
        };

        carregarDados();
    }, [cpf]);

    function gerarRecibo() {
        const data = { ...formik.values };
        data.campanhaMudas = pessoaMudas;
        const win = window.open('', '_blank');
        gerarComprovanteEmJanela(data, win);
    }

    const gerarComprovanteEmJanela = async (data, targetWindow) => {
        try {
            const promiseLogos = Promise.all([
                imageToDataURL(LogoAgricultura),
                imageToDataURL(LogoIEF)
            ]);

            let imagemAnexoDataURL = null;

            if (data.arquivo) {
                try {
                    const response = await api.get(`/pessoa/download/${data.arquivo}`, {
                        responseType: 'blob',
                    });
                    const blob = response.data;

                    if (blob && blob.type.startsWith('image/')) {
                        const objectUrl = URL.createObjectURL(blob);
                        imagemAnexoDataURL = await imageToDataURL(objectUrl);
                        URL.revokeObjectURL(objectUrl);
                    }
                } catch (err) {
                    console.error("Erro ao carregar imagem em anexo:", err);
                }
            }

            const [logoAgri, logoIEF] = await promiseLogos;

            const mudasFormatadas = (data.campanhaMudas || []).map(muda => [
                { text: muda.campanhaMuda?.muda?.nome || muda.nome || 'Muda' },
                { text: muda.quantidade, alignment: 'center' },
            ]);

            const dataEntregaPDF = data.dataEntrega || campanhaDataEntrega;
            const enderecoPDF = data.enderecoEntrega || campanhaEndereco;
            const nomeCampanhaPDF = data.nomeCampanha || campanhaNome;

            const documento = {
                pageSize: 'A4',
                pageMargins: [40, 40, 40, 40],

                content: [
                    {
                        columns: [
                            { image: logoAgri, width: 180, height: 55 },
                            { image: logoIEF, width: 180, height: 55, alignment: 'right' },
                        ],
                        columnGap: 100,
                    },
                    {
                        text: `Comprovante de Solicitação: ${nomeCampanhaPDF}\nData de Entrega: ${dataEntregaPDF ? new Date(dataEntregaPDF).toLocaleDateString("pt-BR") : '-'}\nEndereço da Entrega: ${enderecoPDF || '-'}`,
                        style: 'header',
                        alignment: 'center',
                    },

                    { text: 'Dados do Solicitante', style: 'sectionHeader' },
                    {
                        style: 'formContainer',
                        stack: [
                            { text: [{ text: 'Nome: ', bold: true }, data.nome || '-'] },
                            { text: [{ text: 'CPF: ', bold: true }, data.cpf || '-'] },
                            { text: [{ text: 'E-mail: ', bold: true }, data.email || '-'] },
                            { text: [{ text: 'Nº Instalação: ', bold: true }, data.numero_instalacao || '-'] },
                            { text: [{ text: data.rural === true ? 'Fazenda: ' : 'Endereço: ', bold: true }, data.endereco_ou_fazenda || '-'] },
                            { text: [{ text: data.rural === true ? 'Distrito: ' : 'Bairro: ', bold: true }, data.bairro_ou_distrito || '-'] },
                        ]
                    },

                    { text: 'Dados das Mudas Solicitadas', style: 'sectionHeader' },
                    {
                        style: 'table',
                        table: {
                            widths: ['*', 100],
                            body: [
                                [
                                    { text: 'NOME DA MUDA', style: 'tableHeader' },
                                    { text: 'QUANTIDADE', style: 'tableHeader', alignment: 'center' }
                                ],
                                ...mudasFormatadas
                            ],
                        },
                        layout: {
                            fillColor: (i) => (i === 0 ? '#EEEEEE' : null),
                        },
                    },

                    ...(imagemAnexoDataURL ? [
                        { text: '', pageBreak: 'after' },
                        { text: 'Anexo: Comprovante de Endereço / Documento', style: 'sectionHeader', alignment: 'center', margin: [0, 0, 0, 15] },
                        {
                            image: imagemAnexoDataURL,
                            fit: [515, 700],
                            alignment: 'center'
                        }
                    ] : [])
                ],

                styles: {
                    header: { fontSize: 13, bold: true, margin: [0, 15, 0, 15] },
                    sectionHeader: { fontSize: 12, bold: true, margin: [0, 10, 0, 5], color: '#333333' },
                    formContainer: {
                        margin: [0, 0, 0, 15],
                        fontSize: 10,
                        lineHeight: 1.4
                    },
                    table: { margin: [0, 5, 0, 15] },
                    tableHeader: { bold: true, fontSize: 10 }
                },
            };

            const pdfDoc = pdfMake.createPdf(documento);

            if (targetWindow) {
                pdfDoc.getBuffer((buffer) => {
                    const blob = new Blob([buffer], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    targetWindow.location.href = url;
                });
            } else {
                pdfDoc.open();
            }

            pdfDoc.getBase64(async (base64Data) => {
                if (!data.email) return;

                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('@App:token');

                    await api.post(
                        'https://n8n.app.patosdeminas.mg.gov.br/webhook/enviar-pdf',
                        {
                            pdfBase64: base64Data,
                            fileName: `comprovante_solicitacao_${data.cpf || 'mudas'}.pdf`,
                            emailDestinatario: data.email,
                            nomeDestinatario: data.nome,
                            nomeEquipe: 'Equipe do Pomar Doméstico'
                        },
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );
                } catch (error) {
                    console.error('Erro ao enviar requisição para o n8n:', error);
                }
            });

        } catch (error) {
            if (targetWindow) targetWindow.close();
            console.error("Erro ao gerar PDF:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro ao gerar comprovante',
                detail: 'Falha ao carregar anexo ou gerar PDF.'
            });
        }
    };

    const handleClear = () => {
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    const handlerUpload = async (event) => {
        const fileUpload = event.files[0];

        if (!fileUpload || !fileUpload.type.startsWith('image/')) {
            toast.current.show({
                severity: 'error',
                summary: 'Formato inválido',
                detail: 'Apenas arquivos de imagem (PNG, JPEG, JPG) são permitidos.'
            });
            handleClear();
            return;
        }

        let extensao = fileUpload.name.split(".");
        let posicaoExtensao = extensao.length;
        extensao = "_" + (formik.values.numero_instalacao || "DOC") + "." + extensao[posicaoExtensao - 1];

        const file = new File([fileUpload], formik.values.cpf + extensao, { type: fileUpload.type });
        const data = new FormData();

        data.append('file', file);
        data.append('Content-Type', 'multipart/form-data');
        await api.post(`/pessoa/uploadDoc`, data, { "Content-Type": "multipart/form-data" })
            .then(() => {
                toast.current.show({ life: 2000, severity: 'success', summary: 'Arquivo enviado com sucesso!' });
                handleClear();
            });
        formik.setFieldValue("arquivo", file.name);
    };

    const handleDownload = async () => {
        if (!formik.values.arquivo) return;

        try {
            const response = await api.get(`/pessoa/download/${formik.values.arquivo}`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', formik.values.arquivo);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast.current.show({
                life: 3000,
                severity: 'error',
                summary: 'Erro ao baixar o arquivo',
                detail: error.message
            });
        }
    };

    return (
        <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
            <Toast ref={toast} position="bottom-right" />
            <div className="p-col-12">
                <h2 className="p-mt-0">{somenteComprovante ? "Comprovante de Solicitação" : "Formulário Solicitar Mudas"}</h2>
                {somenteComprovante && (
                    <div className="p-mb-4 p-p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', borderLeft: '4px solid #2196F3' }}>
                        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>Data de Entrega: {campanhaDataEntrega ? new Date(campanhaDataEntrega).toLocaleDateString("pt-BR") : '-'}</p>
                        <p style={{ margin: 0, fontWeight: 'bold' }}>Endereço da Entrega: {campanhaEndereco || '-'}</p>
                    </div>
                )}
                <form onSubmit={formik.handleSubmit}>
                    <div className="p-formgrid p-grid p-fluid p-mx-0">

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="cpf"
                                className={classNames({ 'p-error': isFormFieldValid('cpf') })}
                            >
                                CPF
                            </label>
                            {!isLoading ? (
                                <InputMask
                                    id="cpf"
                                    name="cpf"
                                    mask="999.999.999-99"
                                    unmask={true}
                                    value={formik.values.cpf}
                                    onChange={formik.handleChange}
                                    onComplete={(e) => handleCpfComplete(e.value)}
                                    className={classNames({ 'p-invalid': isFormFieldValid('cpf') })}
                                    style={{ color: '#000' }}
                                    disabled={somenteComprovante}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.cpf && formik.touched.cpf &&
                                <ErrorValidationMessage message={formik.errors.cpf} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="nome"
                                className={classNames({ 'p-error': isFormFieldValid('nome') })}
                            >
                                Nome
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="nome"
                                    name="nome"
                                    className={classNames({ 'p-invalid': isFormFieldValid('nome') })}
                                    value={formik.values.nome}
                                    onChange={formik.handleChange}
                                    style={{ color: '#000' }}
                                    disabled={somenteComprovante}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.nome && formik.touched.nome &&
                                <ErrorValidationMessage message={formik.errors.nome} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="email"
                                className={classNames({ 'p-error': isFormFieldValid('email') })}
                            >
                                E-mail
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="email"
                                    name="email"
                                    type="email"
                                    className={classNames({ 'p-invalid': isFormFieldValid('email') })}
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    placeholder="exemplo@email.com"
                                    style={{ color: '#000' }}
                                    disabled={somenteComprovante}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.email && formik.touched.email &&
                                <ErrorValidationMessage message={formik.errors.email} />
                            }
                        </div>

                        {validarEnderecoPessoa ? (
                            <div className="p-field p-col-12 p-sm-4">
                                <label
                                    htmlFor="numero_instalacao"
                                    className={classNames({ 'p-error': isFormFieldValid('numero_instalacao') })}
                                >
                                    Nº da Instalação (CEMIG)
                                </label>
                                {!isLoading ? (
                                    <InputMask
                                        id="numero_instalacao"
                                        name="numero_instalacao"
                                        mask="9999999999"
                                        unmask={true}
                                        value={formik.values.numero_instalacao}
                                        onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('numero_instalacao') })}
                                        style={{ color: '#000' }}
                                        disabled={somenteComprovante}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                                {formik.errors.numero_instalacao && formik.touched.numero_instalacao &&
                                    <ErrorValidationMessage message={formik.errors.numero_instalacao} />
                                }
                            </div>
                        ) : (
                            <div className="p-field p-col-12 p-sm-4">
                                <label htmlFor="numero_instalacao">
                                    Nº da Instalação (Opcional)
                                </label>
                                {!isLoading ? (
                                    <InputMask
                                        id="numero_instalacao"
                                        name="numero_instalacao"
                                        mask="9999999999"
                                        unmask={true}
                                        value={formik.values.numero_instalacao}
                                        onChange={formik.handleChange}
                                        style={{ color: '#000' }}
                                        disabled={somenteComprovante}
                                    />
                                ) : (
                                    <Skeleton height="35px" />
                                )}
                            </div>
                        )}

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="rural"
                                className={classNames({ 'p-error': isFormFieldValid('rural') })}
                            >
                                Zona
                            </label>
                            {!isLoading ? (
                                <Dropdown
                                    id="rural"
                                    name="rural"
                                    options={["URBANO", "RURAL"]}
                                    emptyMessage={"Nenhuma zona encontrada"}
                                    placeholder="Selecione um zona"
                                    className={classNames({ 'p-invalid': isFormFieldValid('rural') })}
                                    value={formik.values.rural === true ? "RURAL" : formik.values.rural === false ? "URBANO" : ""}
                                    onChange={(e) => formik.setFieldValue("rural", e.value === "RURAL" ? true : e.value === "URBANO" ? false : "")}
                                    emptyFilterMessage={"Nenhuma zona encontrada"}
                                    disabled={somenteComprovante}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.rural && formik.touched.rural &&
                                <ErrorValidationMessage message={formik.errors.rural} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="endereco_ou_fazenda"
                                className={classNames({ 'p-error': isFormFieldValid('endereco_ou_fazenda') })}
                            >
                                {formik.values.rural === true ? "Fazenda" : formik.values.rural === false ? "Endereço" : "Endereço ou Fazenda"}
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="endereco_ou_fazenda"
                                    name="endereco_ou_fazenda"
                                    className={classNames({ 'p-invalid': isFormFieldValid('endereco_ou_fazenda') })}
                                    value={formik.values.endereco_ou_fazenda}
                                    onChange={formik.handleChange}
                                    style={{ color: '#000' }}
                                    disabled={somenteComprovante || formik.values.rural === ""}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.endereco_ou_fazenda && formik.touched.endereco_ou_fazenda &&
                                <ErrorValidationMessage message={formik.errors.endereco_ou_fazenda} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="bairro_ou_distrito"
                                className={classNames({ 'p-error': isFormFieldValid('bairro_ou_distrito') })}
                            >
                                {formik.values.rural === true ? "Distrito" : formik.values.rural === false ? "Bairro" : "Bairro ou Distrito"}
                            </label>
                            {!isLoading ? (
                                <InputText
                                    id="bairro_ou_distrito"
                                    name="bairro_ou_distrito"
                                    className={classNames({ 'p-invalid': isFormFieldValid('bairro_ou_distrito') })}
                                    value={formik.values.bairro_ou_distrito}
                                    onChange={formik.handleChange}
                                    style={{ color: '#000' }}
                                    disabled={somenteComprovante || formik.values.rural === ""}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.bairro_ou_distrito && formik.touched.bairro_ou_distrito &&
                                <ErrorValidationMessage message={formik.errors.bairro_ou_distrito} />
                            }
                        </div>

                        {validarEnderecoPessoa && (
                            <div className="p-field p-col-12 p-sm-4">
                                <label
                                    htmlFor="arquivo"
                                    className={classNames({ 'p-error': isFormFieldValid('arquivo') })}>
                                    {somenteComprovante ? "Comprovante de Endereço Anexado" : "Selecione o Comprovante (Formatos JPEG, JPG, PNG ou WEBP)"}
                                </label>
                                {!somenteComprovante && (
                                    <FileUpload
                                        ref={fileUploadRef}
                                        mode="basic"
                                        name="file"
                                        auto={true}
                                        customUpload
                                        accept="image/jpeg,image/png,image/jpg,image/webp"
                                        uploadHandler={handlerUpload}
                                        chooseLabel="Anexar Comprovante de Endereço"
                                        className="p-button-primary"
                                    />
                                )}
                                {formik.values.arquivo && (
                                    <div className="p-mt-2">
                                        <Button
                                            type="button"
                                            label={`Baixar Comprovante (${formik.values.arquivo})`}
                                            icon="pi pi-download"
                                            className="p-button-outlined p-button-info"
                                            onClick={handleDownload}
                                        />
                                    </div>
                                )}
                                {formik.errors.arquivo && formik.touched.arquivo &&
                                    <ErrorValidationMessage message={formik.errors.arquivo} />
                                }
                            </div>
                        )}

                    </div>

                    <div className="p-col-12 p-mt-4">
                        {!somenteComprovante && (
                            <>
                                <h3>Escolha das Mudas</h3>
                                <FormPessoaMudas
                                    listMudas={listMudas}
                                    setListMudas={setListMudas}
                                    pessoaMudas={pessoaMudas}
                                    setPessoaMudas={setPessoaMudas}
                                    rural={formik.values.rural}
                                    quantidadeTotal={quantidadeTotal}
                                    setQuantidadeTotal={setQuantidadeTotal}
                                    isLoading={isLoading}
                                    isEditing={isEditing}
                                />
                            </>
                        )}

                        <h3>Mudas Solicitadas</h3>
                        <TabelaPessoaMudas
                            pessoaMudas={pessoaMudas}
                            setPessoaMudas={setPessoaMudas}
                            listMudas={listMudas}
                            setListMudas={setListMudas}
                            setQuantidadeTotal={setQuantidadeTotal}
                            disabled={somenteComprovante}
                        />
                    </div>

                    <div className="p-d-flex p-jc-end p-mt-4">
                        <Button
                            type="button"
                            label="Voltar"
                            icon="pi pi-arrow-left"
                            className="p-button-secondary p-mr-2"
                            onClick={() => setSolicitarMudas(false)}
                        />
                        {somenteComprovante ? (
                            <Button
                                type="button"
                                label="Reimprimir Comprovante (PDF)"
                                icon="pi pi-print"
                                className="p-button-success"
                                onClick={gerarRecibo}
                            />
                        ) : (
                            <Button
                                type="submit"
                                label={isSubmiting ? "Enviando..." : "Finalizar Solicitação"}
                                icon="pi pi-check"
                                className="p-button-primary"
                                loading={isSubmiting}
                            />
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};